import { ApiError } from '@ottd/api';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { postResendVerification } from '@/auth/api';

const HTTP_TOO_MANY_REQUESTS = 429;
const SECONDS_PER_MINUTE = 60;

/**
 * The backend allows one resend per account per five minutes (legacy
 * `throttle:1,5`); a 429 without a usable `Retry-After` falls back to that.
 */
const RESEND_WINDOW_MINUTES = 5;

/** Whole minutes until the next resend is allowed, rounded up, at least 1. */
export function minutesUntilResend(retryAfterSeconds: number | null): number {
  if (retryAfterSeconds === null || retryAfterSeconds <= 0) return RESEND_WINDOW_MINUTES;
  return Math.max(1, Math.ceil(retryAfterSeconds / SECONDS_PER_MINUTE));
}

/**
 * "Resend verification email" for every screen that offers it. A throttled
 * resend is not a failure - the last link is on its way - so it says when the
 * next one can be requested instead of the generic error.
 */
export function useResendVerification(): { sending: boolean; resend: () => Promise<void> } {
  const { t } = useTranslation('auth');
  const [sending, setSending] = useState(false);

  const resend = useCallback(async () => {
    setSending(true);
    try {
      await postResendVerification();
      toast.success(t('verifyEmail.resent'));
    } catch (error) {
      if (error instanceof ApiError && error.status === HTTP_TOO_MANY_REQUESTS) {
        toast.error(
          t('verifyEmail.resendThrottled', { count: minutesUntilResend(error.retryAfter) }),
        );
      } else {
        toast.error(t('errors.generic'));
      }
    } finally {
      setSending(false);
    }
  }, [t]);

  return { sending, resend };
}
